import {useState, useEffect, FC, useMemo, PropsWithChildren} from 'react';
import { useParams, useNavigate } from 'react-router';
import { toastr } from '@/utils/toastHelper';
import { sendGTMEvent } from '@services/helpers';
import { auth } from '@services/auth';
import { ConfirmEmailProvider, ConfirmEmailState } from './context';
import { injectChild } from '@components/constructor';
import {useMetrics, useThemeUrl} from '@hooks/panelHooks';
import {useLocalizedBlock} from '@services/hooks';

const ConfirmEmail: FC<PropsWithChildren> = ({ children }) => {

    const [state, setState] = useState<ConfirmEmailState>({
        done: false,
        error: false,
        fetching: true,
        success: false
    });

    const { code } = useParams<{ code: string }>();
    const navigate = useNavigate();
    const redirectUrl = useThemeUrl('confirm.success');
    const locale = useLocalizedBlock('guest.email');

    const { reachGoal } = useMetrics();

    useEffect(() => {

        const confirm = async () => {
            try {
                const result = await auth().confirmEmail(code || '');
                const success = result.ok === true;

                if (success) {
                    sendGTMEvent('formsend');
                    toastr.success(locale['toast.header'], locale['toast.success']);
                    reachGoal('confirm');
                    navigate(redirectUrl || '/auth/', { replace: true });
                }

                setState({
                    fetching: false,
                    done: true,
                    success,
                    error: false
                });
            }
            catch (e) {
                setState({
                    fetching: false,
                    done: true,
                    success: false,
                    error: true
                });
            }
        }

        confirm();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const data = useMemo(() => {
        return {
            email_confirming: state.fetching,
            email_confirmed: state.success,
            email_error: state.error
        }
    }, [state.error, state.fetching, state.success]);

    return (
        <ConfirmEmailProvider value={state}>
            {injectChild(data, children)}
        </ConfirmEmailProvider>
    )
}

export default ConfirmEmail;